/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

class BBWishlistManager {
  static ATTRIBUTE_KEY = '_bb_wishlist';
  static DEBOUNCE_MS = 300;

  constructor() {
    this.handles = [];
    this.initialized = false;
    this.debounceTimer = null;
    this.pendingUpdate = null;
    this.removedSinceSync = new Set();
    const seed = (typeof window !== 'undefined' && window.bbWishlistInitial) || null;
    if (Array.isArray(seed)) {
      this.handles = seed.filter(Boolean);
      this.initialized = true;
    }
  }

  init() {
    if (this.initialized) return Promise.resolve(this.handles);
    if (this._initPromise) return this._initPromise;
    this._initPromise = fetch(`${Shopify.routes.root}cart.js`)
      .then((res) => res.json())
      .then((cart) => {
        const raw = cart.attributes?.[BBWishlistManager.ATTRIBUTE_KEY] || '';
        this.handles = raw ? raw.split(',').filter(Boolean) : [];
        this.initialized = true;
        this.dispatch();
        return this.handles;
      })
      .catch(() => {
        this.handles = [];
        this.initialized = true;
        this.dispatch();
        return this.handles;
      });
    return this._initPromise;
  }

  save() {
    const removals = new Set(this.removedSinceSync);
    this.removedSinceSync.clear();

    this.pendingUpdate = fetch(`${Shopify.routes.root}cart.js`)
      .then((res) => res.json())
      .then((cart) => {
        const raw = cart.attributes?.[BBWishlistManager.ATTRIBUTE_KEY] || '';
        const serverHandles = raw ? raw.split(',').filter(Boolean) : [];
        const merged = [...new Set([...serverHandles, ...this.handles])];
        this.handles = merged.filter((h) => !removals.has(h));

        const value = this.handles.join(',');
        return fetch(`${Shopify.routes.root}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { [BBWishlistManager.ATTRIBUTE_KEY]: value } }),
        });
      })
      .then(() => {
        this.pendingUpdate = null;
        this.dispatch();
      })
      .catch(() => {
        this.pendingUpdate = null;
      });
  }

  dispatch() {
    document.dispatchEvent(
      new CustomEvent('wishlist:change', {
        detail: { handles: [...this.handles] },
      }),
    );
  }

  debouncedSave() {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.save(), BBWishlistManager.DEBOUNCE_MS);
  }

  get() {
    return [...this.handles];
  }

  has(handle) {
    return this.handles.includes(handle);
  }

  add(handle) {
    if (!handle || this.handles.includes(handle)) return;
    this.removedSinceSync.delete(handle);
    this.handles.push(handle);
    this.dispatch();
    this.debouncedSave();
  }

  remove(handle) {
    const idx = this.handles.indexOf(handle);
    if (idx === -1) return;
    this.removedSinceSync.add(handle);
    this.handles.splice(idx, 1);
    this.dispatch();
    this.debouncedSave();
  }

  toggle(handle) {
    if (this.handles.includes(handle)) {
      this.remove(handle);
      return false;
    } else {
      this.add(handle);
      return true;
    }
  }

  set(newHandles) {
    this.handles = [...newHandles];
    this.dispatch();
    this.debouncedSave();
  }
}

const BBWishlist = new BBWishlistManager();

class BBWishlistButton extends HTMLElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
    this._onWishlistChange = this._onWishlistChange.bind(this);
  }

  connectedCallback() {
    const handle = this.getAttribute('handle');
    // Sync any drift between server-rendered data-active and live state without animating.
    const currentActive = this.dataset.active === 'true';
    const liveActive = BBWishlist.has(handle);
    if (currentActive !== liveActive) {
      this.dataset.noTransition = 'true';
      this.dataset.active = liveActive.toString();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => delete this.dataset.noTransition);
      });
    }
    BBWishlist.init();
    this.addEventListener('click', this._onClick);
    document.addEventListener('wishlist:change', this._onWishlistChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    document.removeEventListener('wishlist:change', this._onWishlistChange);
  }

  _onClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const handle = this.getAttribute('handle');
    if (!handle) return;
    const added = BBWishlist.toggle(handle);
    if (added) {
      this.dispatchEvent(new CustomEvent('wishlist:added', { bubbles: true, detail: { handle } }));
    }
  }

  _onWishlistChange(e) {
    this.dataset.active = e.detail.handles.includes(this.getAttribute('handle')).toString();
  }
}

if (!customElements.get('bb-wishlist-button')) {
  customElements.define('bb-wishlist-button', BBWishlistButton);
}
class BBWishlistShareButton extends HTMLElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
    this._onChange = this._onChange.bind(this);
  }

  connectedCallback() {
    BBWishlist.init().then((handles) => this._update(handles));
    this.addEventListener('click', this._onClick);
    document.addEventListener('wishlist:change', this._onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    document.removeEventListener('wishlist:change', this._onChange);
  }

  _onChange(e) {
    this._update(e.detail.handles);
  }

  _update(handles) {
    this.hidden = !handles.length;
  }

  _onClick(e) {
    e.preventDefault();
    BBWishlist.init().then((handles) => {
      if (!handles.length) return;
      const url = new URL(window.location.href);
      url.search = '';
      url.hash = '';
      url.searchParams.set('wishlist', handles.join(','));
      const shareUrl = url.toString();

      if (navigator.share) {
        navigator.share({ url: shareUrl }).catch(() => {
          this._copyFallback(shareUrl);
        });
      } else {
        this._copyFallback(shareUrl);
      }
    });
  }

  _copyFallback(url) {
    navigator.clipboard.writeText(url).catch(() => {});
  }
}

if (!customElements.get('bb-wishlist-share-button')) {
  customElements.define('bb-wishlist-share-button', BBWishlistShareButton);
}

class BBWishlistCount extends HTMLElement {
  constructor() {
    super();
    this._onChange = this._onChange.bind(this);
  }

  connectedCallback() {
    BBWishlist.init().then((handles) => this._update(handles));
    document.addEventListener('wishlist:change', this._onChange);
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist:change', this._onChange);
  }

  _onChange(e) {
    this._update(e.detail.handles);
  }

  _update(handles) {
    const count = handles.length;
    this.textContent = count;
    this.hidden = count === 0;
  }
}

if (!customElements.get('bb-wishlist-count')) {
  customElements.define('bb-wishlist-count', BBWishlistCount);
}

/* wishlist import ?wishlist= query param */
(function () {
  const params = new URLSearchParams(window.location.search);
  const wl = params.get('wishlist');
  if (!wl) return;

  const handles = wl.split(',').filter(Boolean);
  if (!handles.length) return;

  fetch(`${Shopify.routes.root}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ attributes: { _bb_wishlist: handles.join(',') } }),
  })
    .then(() => {
      const url = new URL(window.location.href);
      url.searchParams.delete('wishlist');
      window.location.replace(url.toString());
    })
    .catch(() => {
      const url = new URL(window.location.href);
      url.searchParams.delete('wishlist');
      window.location.replace(url.toString());
    });
})();

class BBSocialCarousel extends HTMLElement {
  constructor() {
    super();
    this._onPrev = this._onPrev.bind(this);
    this._onNext = this._onNext.bind(this);
    this._onScroll = this._onScroll.bind(this);
  }

  connectedCallback() {
    this.scroller = this.querySelector('.bb-social-grid__scroller');
    this.prevNext = this.querySelector('prev-next-buttons');
    if (!this.scroller) return;

    this.addEventListener('prev-next:prev', this._onPrev);
    this.addEventListener('prev-next:next', this._onNext);
    this.scroller.addEventListener('scroll', this._onScroll, { passive: true });
    requestAnimationFrame(() => this._updateButtons());
  }

  disconnectedCallback() {
    this.removeEventListener('prev-next:prev', this._onPrev);
    this.removeEventListener('prev-next:next', this._onNext);
    if (this.scroller) this.scroller.removeEventListener('scroll', this._onScroll);
  }

  _step() {
    const item = this.scroller.querySelector('.bb-social-grid__item');
    if (!item) return this.scroller.clientWidth;
    const styles = getComputedStyle(this.scroller.querySelector('.bb-social-grid__list--carousel') || this.scroller);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return item.getBoundingClientRect().width + gap;
  }

  _onPrev() {
    this.scroller.scrollBy({ left: -this._step(), behavior: 'smooth' });
  }

  _onNext() {
    this.scroller.scrollBy({ left: this._step(), behavior: 'smooth' });
  }

  _onScroll() {
    this._updateButtons();
  }

  _updateButtons() {
    if (!this.prevNext) return;
    const max = this.scroller.scrollWidth - this.scroller.clientWidth;
    const left = Math.abs(this.scroller.scrollLeft);
    this.prevNext.isPrevDisabled = left <= 1;
    this.prevNext.isNextDisabled = left >= max - 1;
  }
}

if (!customElements.get('bb-social-carousel')) {
  customElements.define('bb-social-carousel', BBSocialCarousel);
}

class BBCustomize extends HTMLElement {
  constructor() {
    super();
    this.draft = { material: '', name: '', symbol: 'ohne' };
    this._symbolLabels = {};
    this._symbolTemplates = {};
    this._onVariantAdded = this._onVariantAdded.bind(this);
  }

  connectedCallback() {
    this._parseConfig();

    this._productForm = this._productFormId ? document.getElementById(this._productFormId) : null;
    this._resolveFormRefs();

    this._cacheNodes();
    this._collectSymbolAssets();
    this._readDraftFromDOM();
    this._updateCounter();
    this._syncPreviewImage();
    this._syncPreview();
    this._updateDefaultCta();
    this._updateMainCtaDisabled();
    this._updateMainCtaText();
    this._bindEvents();

    if (this.dataset.layout === 'static' && this._dynamicCheckout) {
      this._dynamicCheckout.hidden = true;
    }

    if (this._productForm) {
      this._productForm.addEventListener('variant:added', this._onVariantAdded);
    }
  }

  disconnectedCallback() {
    if (this._productForm) {
      this._productForm.removeEventListener('variant:added', this._onVariantAdded);
    }
    this._removeFingerprintInput();
    this._removePropertyInputs();
  }

  get mode() {
    return this.dataset.state || 'default';
  }

  _parseConfig() {
    const script = this.querySelector('script[data-config]');
    let cfg = {};
    try {
      cfg = script ? JSON.parse(script.textContent) : {};
    } catch (e) {
      cfg = {};
    }
    this._productFormId = this.dataset.productFormId || '';
    this._extraProduct = cfg.extraProduct !== false;
    const nameCfg = cfg.name || {};
    this._nameMax = parseInt(nameCfg.max, 10) || 12;
    this._nameMin = parseInt(nameCfg.min, 10) || 0;
    this._nameRequired = !!nameCfg.required;
    try {
      this._nameRegex = nameCfg.regex ? new RegExp(nameCfg.regex) : null;
    } catch (e) {
      this._nameRegex = null;
    }
    this._propKeys = cfg.propKeys || {};
    this._errors = cfg.errors || {};
  }

  _cacheNodes() {
    this._bodyWrap = this.querySelector('[data-body-wrap]');
    this._toggleTriggers = Array.from(this.querySelectorAll('[data-action="toggle"]'));
    this._materialRadios = Array.from(this.querySelectorAll('[data-field="material"]'));
    this._symbolRadios = Array.from(this.querySelectorAll('[data-field="symbol"]'));
    this._nameInput = this.querySelector('[data-field="name"]');
    this._nameCounter = this.querySelector('[data-name-counter]');
    this._nameError = this.querySelector('[data-field-error="name"]');
    this._defaultCta = this.querySelector('[data-default-cta]');
    this._previewSymbol = this.querySelector('[data-preview-symbol]');
    this._previewName = this.querySelector('[data-preview-name]');
    this._previewMaterial = this.querySelector('[data-preview-material]');
    this._previewImages = Array.from(this.querySelectorAll('[data-preview-for]'));
    this._previewPlaceholder = this.querySelector('[data-preview-placeholder]');
  }

  _collectSymbolAssets() {
    this.querySelectorAll('template[data-symbol-template]').forEach((t) => {
      const el = t.content.firstElementChild;
      if (el) this._symbolTemplates[t.dataset.symbolTemplate] = el.outerHTML;
    });
    this.querySelectorAll('template[data-symbol-label]').forEach((t) => {
      this._symbolLabels[t.dataset.symbolLabel] = (t.content.textContent || '').trim();
    });
  }

  _readDraftFromDOM() {
    const m = this._materialRadios.find((r) => r.checked);
    const s = this._symbolRadios.find((r) => r.checked);
    this.draft = {
      material: m ? m.value : '',
      name: this._nameInput ? this._nameInput.value : '',
      symbol: s ? s.value : 'ohne',
    };
  }

  _bindEvents() {
    this._toggleTriggers.forEach((b) => {
      b.addEventListener('click', () => (this.mode === 'opened' ? this._cancel() : this._open()));
    });
    this.querySelectorAll('[data-action="cancel"]').forEach((b) => b.addEventListener('click', () => this._cancel()));

    if (this._nameInput) {
      this._nameInput.addEventListener('input', () => {
        this.draft.name = this._nameInput.value;
        this._updateCounter();
        this._applyNameError(this._computeNameError(false));
        this._syncPreview();
        this._updateMainCtaDisabled();
      });
    }

    this._materialRadios.forEach((r) => {
      r.addEventListener('change', () => {
        if (!r.checked) return;
        this.draft.material = r.value;
        this._syncPreviewImage();
        this._syncPreview();
        this._updateDefaultCta();
        this._updateMainCtaDisabled();
        this._updateMainCtaText();
      });
    });

    this._symbolRadios.forEach((r) => {
      r.addEventListener('change', () => {
        if (!r.checked) return;
        this.draft.symbol = r.value;
        this._syncPreview();
      });
    });
  }

  _open() {
    this._setMode('opened');
    if (this._nameInput) this._nameInput.focus({ preventScroll: true });
  }

  _cancel() {
    this._setMode('default');
  }

  _onVariantAdded(event) {
    if (!this._extraProduct) return;
    if (this.mode !== 'opened' || !this._isSatisfied()) return;

    const radio = this._currentVariantRadio();
    if (!radio || !radio.dataset.variantId) return;

    // The product form's add-to-cart response carries the parent line item (incl. its cart line key).
    const parentLine = event && event.detail ? event.detail.variant : null;
    this._addPersonalizationToCart(radio.dataset.variantId, parentLine ? parentLine.key : '');
  }

  _addPersonalizationToCart(variantId, parentLineKey) {
    const properties = {
      [this._propKeys.material]: this.draft.material,
      [this._propKeys.name]: this.draft.name,
      [this._propKeys.symbol]: this._symbolLabels[this.draft.symbol] || this.draft.symbol,
    };

    const item = { id: variantId, quantity: this._currentQuantity(), properties: properties };
    // Nested cart line: attach the personalization addon as a child of the product line just added.
    if (parentLineKey) item.parent_line_key = parentLineKey;

    const routes = window.themeVariables.routes;

    return fetch(routes.cartAddUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [item], sections: ['mini-cart'] }),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((responseJson) =>
        fetch(routes.cartUrl + '.js')
          .then((res) => res.json())
          .then((cartContent) => {
            cartContent['sections'] = responseJson['sections'];
            document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cartContent } }));
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cartContent } }));
            return true;
          })
      )
      .catch(() => {
        this.dispatchEvent(new CustomEvent('bb-customize:error', { bubbles: true }));
        return false;
      });
  }

  _setMode(mode) {
    this.dataset.state = mode;

    this.querySelectorAll('[data-show-states]').forEach((el) => {
      el.hidden = !el.dataset.showStates.split(/\s+/).includes(mode);
    });

    if (this._bodyWrap) {
      if (mode === 'opened') {
        this._bodyWrap.setAttribute('data-active', '');
        this._bodyWrap.removeAttribute('inert');
      } else {
        this._bodyWrap.removeAttribute('data-active');
        this._bodyWrap.setAttribute('inert', '');
      }
    }

    const expanded = mode === 'opened' ? 'true' : 'false';
    this._toggleTriggers.forEach((btn) => btn.setAttribute('aria-expanded', expanded));

    if (this._dynamicCheckout) {
      this._dynamicCheckout.hidden = mode === 'opened';
    }

    this._syncFormProperty();
    this._updateMainCtaDisabled();
    this._updateMainCtaText();
  }

  _updateCounter() {
    if (!this._nameCounter) return;
    this._nameCounter.textContent = `${(this.draft.name || '').length}/${this._nameMax}`;
  }

  _syncPreviewImage() {
    const radio = this._materialRadios.find((r) => r.checked);
    const index = radio ? radio.dataset.previewIndex || '' : '';
    let shown = false;
    this._previewImages.forEach((img) => {
      const active = index !== '' && img.dataset.previewFor === index;
      img.hidden = !active;
      if (active) shown = true;
    });
    if (this._previewPlaceholder) this._previewPlaceholder.hidden = shown;
  }

  _syncPreview() {
    if (this._previewName) this._previewName.textContent = this.draft.name || '';
    if (this._previewSymbol) {
      const html = this.draft.symbol && this.draft.symbol !== 'ohne' ? this._symbolTemplates[this.draft.symbol] || '' : '';
      this._previewSymbol.innerHTML = html;
    }
    if (this._previewMaterial) this._previewMaterial.textContent = this.draft.material || '';
    this._syncFormProperty();
  }

  /* While the customizer is open, the product form carries a hidden "private" line item
     property (underscore prefix = hidden at checkout, filtered by the cart templates) that
     fingerprints the personalization config. Without it, Shopify merges same-variant parent
     lines, stacking differently-personalized addons under one parent. Identical configs
     still merge cleanly (parent + nested addon quantities both stack). */
  _syncFormProperty() {
    if (!this._productForm) return;

    if (this._extraProduct) {
      this._syncFingerprintProperty();
    } else {
      this._syncVisibleProperties();
    }
  }

  _syncFingerprintProperty() {
    if (this.mode === 'opened') {
      if (!this._fingerprintInput || !this._fingerprintInput.isConnected) {
        this._fingerprintInput = document.createElement('input');
        this._fingerprintInput.type = 'hidden';
        this._fingerprintInput.name = 'properties[_bb_personalization]';
        this._productForm.appendChild(this._fingerprintInput);
      }
      const symbol = this._symbolLabels[this.draft.symbol] || this.draft.symbol;
      this._fingerprintInput.value = [this.draft.material, (this.draft.name || '').trim(), symbol].join(' | ');
    } else {
      this._removeFingerprintInput();
    }
  }

  _syncVisibleProperties() {
    this._removePropertyInputs();
    if (this.mode !== 'opened') return;

    const name = (this.draft.name || '').trim();
    const symbol =
      this.draft.symbol && this.draft.symbol !== 'ohne' ? this._symbolLabels[this.draft.symbol] || this.draft.symbol : '';

    const entries = [
      [this._propKeys.material, this.draft.material],
      [this._propKeys.name, name],
      [this._propKeys.symbol, symbol],
    ];

    this._propertyInputs = [];
    entries.forEach(([key, value]) => {
      if (!key || !value) return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${key}]`;
      input.value = value;
      input.setAttribute('data-bb-property', '');
      this._productForm.appendChild(input);
      this._propertyInputs.push(input);
    });
  }

  _removeFingerprintInput() {
    if (this._fingerprintInput) {
      this._fingerprintInput.remove();
      this._fingerprintInput = null;
    }
  }

  _removePropertyInputs() {
    if (this._propertyInputs) {
      this._propertyInputs.forEach((input) => input.remove());
      this._propertyInputs = null;
    }
  }

  _updateDefaultCta() {
    if (!this._defaultCta) return;
    const radio = this._currentVariantRadio();
    const text = radio && radio.dataset.defaultCtaText;
    if (text) this._defaultCta.textContent = text;
  }

  _computeNameError(strict) {
    const name = (this.draft.name || '').trim();
    if (this._nameRegex && name && !this._nameRegex.test(name)) {
      return this._errors.regex || '';
    }
    if (strict && this._nameRequired) {
      if (!name) return this._errors.required || '';
      if (name.length < this._nameMin) {
        return (this._errors.tooShort || '').replace(/%min%/g, String(this._nameMin));
      }
    }
    return '';
  }

  _applyNameError(error) {
    if (!this._nameError) return;
    if (error) {
      this._nameError.textContent = error;
      this._nameError.removeAttribute('hidden');
      if (this._nameInput) this._nameInput.setAttribute('aria-invalid', 'true');
    } else {
      this._nameError.textContent = '';
      this._nameError.setAttribute('hidden', '');
      if (this._nameInput) this._nameInput.removeAttribute('aria-invalid');
    }
  }

  _isSatisfied() {
    const name = (this.draft.name || '').trim();
    const radio = this._currentVariantRadio();
    const variantOk = !!radio && radio.dataset.variantAvailable === 'true' && !!radio.dataset.variantId;
    const error = this._computeNameError(false);
    const nameOk = !error && (!this._nameRequired || name.length >= Math.max(1, this._nameMin));
    return !!(this.draft.material && variantOk && nameOk);
  }

  _updateMainCtaDisabled() {
    this._resolveFormRefs();
    if (!this._addToCartBtn) return;
    if (this.mode !== 'opened') {
      if (this._ctaHeld) {
        this._addToCartBtn.disabled = false;
        this._ctaHeld = false;
      }
      return;
    }
    const satisfied = this._isSatisfied();
    this._addToCartBtn.disabled = !satisfied;
    this._ctaHeld = !satisfied;
  }

  _resolveFormRefs() {
    if (!this._productForm) return;

    if (!this._addToCartBtn || !this._addToCartBtn.isConnected) {
      this._addToCartBtn = this._productForm.querySelector('[data-product-add-to-cart-button]');
      this._mainCtaTextEl = this._addToCartBtn ? this._addToCartBtn.querySelector('.loader-button__text') || this._addToCartBtn : null;
      this._mainCtaBaseText = this._mainCtaTextEl ? (this._mainCtaTextEl.textContent || '').trim() : '';
    }

    if (!this._dynamicCheckout || !this._dynamicCheckout.isConnected) {
      this._dynamicCheckout = this._productForm.querySelector('.shopify-payment-button');
    }
  }

  _updateMainCtaText() {
    this._resolveFormRefs();
    if (!this._mainCtaTextEl) return;

    if (this.mode === 'opened' && this._extraProduct) {
      const radio = this._currentVariantRadio();
      const suffix = radio ? (radio.dataset.totalSuffix || radio.dataset.personalizedSuffix || '').trim() : '';
      this._mainCtaTextEl.textContent = suffix ? `${this._mainCtaBaseText} ${suffix}` : this._mainCtaBaseText;
    } else {
      this._mainCtaTextEl.textContent = this._mainCtaBaseText;
    }
  }

  _currentVariantRadio() {
    return this._materialRadios.find((r) => r.checked) || null;
  }

  _currentQuantity() {
    this._resolveFormRefs();
    if (!this._productForm) return 1;
    const raw = new FormData(this._productForm).get('quantity');
    const qty = parseInt(raw, 10);
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }
}

if (!customElements.get('bb-customize')) {
  customElements.define('bb-customize', BBCustomize);
}

/* Nested cart line quantity sync.
   Parent line items that carry nested addon lines get data-bb-parent-key / data-bb-child-keys
   (mini-cart.liquid + main-cart.liquid). Quantity changes on such parents are intercepted in
   the capture phase (before Focal's LineItemQuantity delegate) and applied to the parent AND
   all of its children in a single /cart/update.js call keyed by line item keys.
   Removal (quantity 0) is left to the native flow: Shopify removes nested children automatically. */
(() => {
  function parentLineItemFor(target) {
    if (!(target instanceof Element)) return null;
    if (target.closest('.bb-cart-subitem')) return null;
    return target.closest('[data-bb-child-keys]');
  }

  async function syncQuantity(lineItemEl, sourceEl, quantity) {
    const routes = window.themeVariables.routes;
    const updates = {};
    updates[lineItemEl.getAttribute('data-bb-parent-key')] = quantity;
    (lineItemEl.getAttribute('data-bb-child-keys') || '')
      .split(',')
      .filter(Boolean)
      .forEach((key) => {
        updates[key] = quantity;
      });

    sourceEl.dispatchEvent(new CustomEvent('line-item-quantity:change:start', { bubbles: true, detail: { newLineQuantity: quantity } }));

    const response = await fetch(`${routes.cartUpdateUrl || '/cart/update'}.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates, sections: ['mini-cart'] }),
    });

    if (!response.ok) {
      sourceEl.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      sourceEl.dispatchEvent(new CustomEvent('line-item-quantity:change:end', { bubbles: true }));
      return;
    }

    if (window.themeVariables.settings.pageType === 'cart') {
      window.location.reload();
      return;
    }

    const cartContent = await response.json();
    sourceEl.dispatchEvent(new CustomEvent('line-item-quantity:change:end', { bubbles: true, detail: { cart: cartContent, newLineQuantity: quantity } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cartContent } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cartContent, replacementDelay: 750 } }));
  }

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target instanceof Element ? event.target.closest('line-item-quantity a[href*="quantity="]') : null;
      if (!link) return;
      const lineItemEl = parentLineItemFor(link);
      if (!lineItemEl) return;

      const url = new URL(link.href, window.location.origin);
      const quantity = parseInt(url.searchParams.get('quantity'), 10);
      if (!Number.isFinite(quantity) || quantity <= 0) return;

      event.preventDefault();
      event.stopPropagation();
      syncQuantity(lineItemEl, link, quantity);
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const input = event.target instanceof Element ? event.target.closest('line-item-quantity input[data-line]') : null;
      if (!input) return;
      const lineItemEl = parentLineItemFor(input);
      if (!lineItemEl) return;

      const quantity = parseInt(input.value, 10);
      if (!Number.isFinite(quantity) || quantity <= 0) return;

      event.stopPropagation();
      syncQuantity(lineItemEl, input, quantity);
    },
    true
  );
})();

class BBCollectionHero extends HTMLElement {
  constructor() {
    super();
    this._update = this._update.bind(this);
  }

  connectedCallback() {
    this._thumbs = Array.from(this.querySelectorAll('[data-collection-hero-thumb]'));
    this._scroller = this.querySelector('.collection-hero__scroller');
    this._update();
    window.addEventListener('popstate', this._update);
    document.addEventListener('cart:refresh', this._update);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this._update);
    document.removeEventListener('cart:refresh', this._update);
  }

  _parseQuery(qs) {
    const params = new URLSearchParams(qs || '');
    return Array.from(params.entries()).sort(([a], [b]) => a.localeCompare(b));
  }

  _matches(blockEntries, urlParams) {
    const effective = blockEntries.filter(([, value]) => value !== '');
    if (!effective.length) return false;
    return effective.every(([key, value]) => urlParams.getAll(key).includes(value));
  }

  _update() {
    const urlParams = new URLSearchParams(window.location.search);
    let activeThumb = null;
    let defaultThumb = null;

    this._thumbs.forEach((thumb) => {
      if (thumb.dataset.default === 'true') {
        defaultThumb = thumb;
        return;
      }
      const entries = this._parseQuery(thumb.dataset.query);
      if (!activeThumb && this._matches(entries, urlParams)) {
        activeThumb = thumb;
      }
    });

    if (!activeThumb) activeThumb = defaultThumb;

    this._thumbs.forEach((thumb) => {
      const isActive = thumb === activeThumb;
      thumb.dataset.active = isActive ? 'true' : 'false';
      if (isActive) {
        thumb.setAttribute('aria-current', 'page');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    if (activeThumb && this._scroller && this._scroller.scrollWidth > this._scroller.clientWidth) {
      const thumbRect = activeThumb.getBoundingClientRect();
      const scrollerRect = this._scroller.getBoundingClientRect();
      const offset = thumbRect.left - scrollerRect.left - (scrollerRect.width - thumbRect.width) / 2 + this._scroller.scrollLeft;
      this._scroller.scrollTo({ left: Math.max(0, offset), behavior: 'instant' in window ? 'instant' : 'auto' });
    }
  }
}

if (!customElements.get('bb-collection-hero')) {
  customElements.define('bb-collection-hero', BBCollectionHero);
}

/* ------------------------------------------------------------------ */
/* BB size guide — switcher (table/finder) + cm slider                 */
/* Rendered once per drawer and once per popover; each instance is     */
/* self-contained (queries within itself, no shared/global state).     */
/* ------------------------------------------------------------------ */
class BBSizeGuide extends HTMLElement {
  constructor() {
    super();
    this._onSwitch = this._onSwitch.bind(this);
    this._onSlide = this._onSlide.bind(this);
  }

  connectedCallback() {
    this._prefix = this.dataset.resultPrefix || '';
    this._unit = this.dataset.unit || '';

    this._switchButtons = Array.from(this.querySelectorAll('.bb-size-guide__switch-button'));
    this._panels = Array.from(this.querySelectorAll('.bb-size-guide__panel'));
    this._switchButtons.forEach((button) => button.addEventListener('click', this._onSwitch));

    this._slider = this.querySelector('[data-bb-sg-slider]');
    const script = this.querySelector('[data-bb-sg-breakpoints]');

    if (this._slider && script) {
      let parsed = [];
      try {
        parsed = JSON.parse(script.textContent) || [];
      } catch (e) {
        parsed = [];
      }

      this._breakpoints = parsed
        .filter((b) => b && Number.isFinite(Number(b.cm_from)) && Number.isFinite(Number(b.cm_to)))
        .map((b) => ({ size: String(b.size == null ? '' : b.size), from: Number(b.cm_from), to: Number(b.cm_to) }))
        .sort((a, b) => a.from - b.from);

      if (this._breakpoints.length) {
        this._initSlider();
      }
    }
  }

  disconnectedCallback() {
    this._switchButtons.forEach((button) => button.removeEventListener('click', this._onSwitch));
    if (this._slider) this._slider.removeEventListener('input', this._onSlide);
  }

  _onSwitch(event) {
    const view = event.currentTarget.dataset.view;
    this._switchButtons.forEach((button) => {
      const isActive = button.dataset.view === view;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    this._panels.forEach((panel) => {
      panel.classList.toggle('is-hidden', panel.dataset.panel !== view);
    });
  }

  _initSlider() {
    const min = this._breakpoints[0].from;
    const max = this._breakpoints[this._breakpoints.length - 1].to;

    this._slider.min = min;
    this._slider.max = max;
    this._slider.value = Math.round((min + max) / 2);

    const minLabel = this.querySelector('[data-bb-sg-min]');
    const maxLabel = this.querySelector('[data-bb-sg-max]');
    if (minLabel) minLabel.textContent = this._format(min);
    if (maxLabel) maxLabel.textContent = this._format(max);

    this._cmValue = this.querySelector('[data-bb-sg-cm]');
    this._resultSize = this.querySelector('[data-bb-sg-size]');
    this._resultRange = this.querySelector('[data-bb-sg-range]');

    this._slider.addEventListener('input', this._onSlide);
    this._render();
  }

  _onSlide() {
    this._render();
  }

  _match(value) {
    const list = this._breakpoints;
    if (value <= list[0].from) return list[0];
    if (value >= list[list.length - 1].to) return list[list.length - 1];
    return list.find((band) => value >= band.from && value <= band.to) || list[list.length - 1];
  }

  _format(value) {
    return this._unit ? `${value} ${this._unit}` : `${value}`;
  }

  _render() {
    const value = Number(this._slider.value);
    const band = this._match(value);

    if (this._cmValue) this._cmValue.textContent = value;
    if (this._resultSize) this._resultSize.textContent = band.size;
    if (this._resultRange) {
      const range = `${band.from}\u2013${band.to}`;
      const parts = [this._prefix, range, this._unit].filter(Boolean);
      this._resultRange.textContent = parts.join(' ');
    }
  }
}

if (!customElements.get('bb-size-guide')) {
  customElements.define('bb-size-guide', BBSizeGuide);
}

/* ------------------------------------------------------------------ */
/* BB hersteller %sku% — re-render the variant SKU inside the          */
/* hersteller rich text whenever the variant changes. Scoped to the    */
/* product-form that emitted the event so quick-buy / sticky forms     */
/* update independently.                                               */
/* ------------------------------------------------------------------ */
document.addEventListener('variant:changed', (event) => {
  const scope = event.target.closest('.product-form');
  if (!scope) return;
  const sku = (event.detail && event.detail.variant && event.detail.variant.sku) || '';
  scope.querySelectorAll('[data-bb-hersteller-sku]').forEach((el) => {
    el.textContent = sku;
  });
});

/* ------------------------------------------------------------------ */
/* BB benefits — reveal the clamped benefits when "Mehr anzeigen" is   */
/* clicked. Expand-only: the button removes itself once expanded.      */
/* ------------------------------------------------------------------ */
class BBBenefitsMore extends HTMLElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    this._button = this.querySelector('.bb-benefits__more');
    if (this._button) this._button.addEventListener('click', this._onClick);
  }

  disconnectedCallback() {
    if (this._button) this._button.removeEventListener('click', this._onClick);
  }

  _onClick() {
    this.classList.add('is-expanded');
  }
}

if (!customElements.get('bb-benefits-more')) {
  customElements.define('bb-benefits-more', BBBenefitsMore);
}

/* ------------------------------------------------------------------ */
/* BB buy-bar — mobile buy-bar wrapping the price, buy buttons and      */
/* inventory. On phone the inner bar floats fixed at the viewport       */
/* bottom (visible from the first viewport) and docks in place once its */
/* anchor scrolls into view. The <bb-buybar> host reserves the bar's    */
/* height so nothing jumps. The price mirrors the main product-meta      */
/* price, which re-renders on variant change.                           */
/* ------------------------------------------------------------------ */
class BBBuyBar extends HTMLElement {
  constructor() {
    super();
    this._onVariantChange = this._onVariantChange.bind(this);
    this._onScroll = this._onScroll.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onMediaChange = this._onMediaChange.bind(this);
    this._enabled = false;
    this._ticking = false;
  }

  connectedCallback() {
    this._formId = this.getAttribute('form-id');
    this._bar = this.querySelector('.bb-buybar__bar');
    this._priceTarget = this.querySelector('.bb-buybar__price');
    this._mql = window.matchMedia('(max-width: 740px)');
    this._syncPrice();
    document.addEventListener('variant:changed', this._onVariantChange);
    if (this._mql.addEventListener) {
      this._mql.addEventListener('change', this._onMediaChange);
    } else {
      this._mql.addListener(this._onMediaChange);
    }
    this._apply();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:changed', this._onVariantChange);
    if (this._mql) {
      if (this._mql.removeEventListener) {
        this._mql.removeEventListener('change', this._onMediaChange);
      } else {
        this._mql.removeListener(this._onMediaChange);
      }
    }
    this._teardown();
  }

  _onMediaChange() {
    this._apply();
  }

  _apply() {
    if (this._mql.matches) {
      this._enable();
    } else {
      this._teardown();
    }
  }

  _enable() {
    if (this._enabled) return;
    this._enabled = true;
    window.addEventListener('scroll', this._onScroll, { passive: true });
    window.addEventListener('resize', this._onResize);
    // Re-evaluate the float/dock state when content above the bar changes height
    // (e.g. the customizer opening), so the bar appears instantly instead of
    // waiting for the first scroll.
    const observeTarget = this.closest('.product-form') || this.parentElement;
    if (window.ResizeObserver && observeTarget) {
      this._resizeObserver = new ResizeObserver(() => this._onScroll());
      this._resizeObserver.observe(observeTarget);
    }
    this._measure();
    this._update();
  }

  _teardown() {
    if (!this._enabled) return;
    this._enabled = false;
    window.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('resize', this._onResize);
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
    this.style.minHeight = '';
    if (this._bar) this._bar.classList.remove('is-floating', 'is-docked');
  }

  _measure() {
    if (!this._bar) return;
    const wasFloating = this._bar.classList.contains('is-floating');
    const wasDocked = this._bar.classList.contains('is-docked');
    // Measure at the docked width (full viewport) so min-height matches the
    // real bar height. Measuring while static uses the narrower content width,
    // which wraps more and over-reserves space (gap above the docked bar).
    this.style.minHeight = '';
    this._bar.classList.remove('is-floating');
    this._bar.classList.add('is-docked');
    const height = this._bar.offsetHeight;
    this._bar.classList.remove('is-docked');
    this.style.minHeight = height + 'px';
    if (wasFloating) this._bar.classList.add('is-floating');
    if (wasDocked) this._bar.classList.add('is-docked');
  }

  _onScroll() {
    if (this._ticking) return;
    this._ticking = true;
    requestAnimationFrame(() => {
      this._update();
      this._ticking = false;
    });
  }

  _onResize() {
    this._measure();
    this._update();
  }

  _update() {
    if (!this._enabled || !this._bar) return;
    if (this.getBoundingClientRect().bottom <= window.innerHeight) {
      this._bar.classList.remove('is-floating');
      this._bar.classList.add('is-docked');
    } else {
      this._bar.classList.remove('is-docked');
      this._bar.classList.add('is-floating');
    }
  }

  _onVariantChange(event) {
    // Note: the theme's variant picker sets detail.formId to form.id, which on
    // product forms is shadowed by the <input name="id"> element (not a string).
    // Compare against the form element's actual id attribute instead.
    const formId = event.target && event.target.getAttribute ? event.target.getAttribute('id') : null;
    if (formId && this._formId && formId !== this._formId) return;
    // The main price re-renders right after variant:changed fires; read it next frame.
    requestAnimationFrame(() => {
      this._syncPrice();
      if (this._enabled) {
        this._measure();
        this._update();
      }
    });
  }

  _syncPrice() {
    if (!this._priceTarget) return;
    const scope = this.closest('product-rerender') || this.closest('.product__info') || document;
    const source = scope.querySelector('.product-meta__price-grid');
    if (source) this._priceTarget.innerHTML = source.innerHTML;
  }
}

if (!customElements.get('bb-buybar')) {
  customElements.define('bb-buybar', BBBuyBar);
}

/* ------------------------------------------------------------------ */
/* BB gift card amount selector — custom variant picker for the native */
/* gift card product (1 option "Wert", 2000 variants "1 €".."2000 €"). */
/* Rendered by snippets/bb-giftcard-amount-selector.liquid on gift     */
/* card products tagged "giftcard-amount-selector".                    */
/*                                                                     */
/* - Preset chips carry their variant IDs from Liquid (all presets sit */
/*   within the 250-variant Liquid cap).                               */
/* - The free amount field resolves variants on demand via the         */
/*   tokenless Storefront API (variantBySelectedOptions), because      */
/*   Liquid/product JSON only expose the first 250 variants.           */
/* - After a selection, the section is re-fetched with ?variant= and   */
/*   the theme-native variant:change / variant:changed /               */
/*   product:rerender events are dispatched so price, sticky form,     */
/*   buy-bar and payment buttons update exactly like with the standard */
/*   picker.                                                           */
/* ------------------------------------------------------------------ */
class BBGiftcardAmountSelector extends HTMLElement {
  static _renderCache = new Map();
  static STOREFRONT_API_VERSION = '2026-04';

  constructor() {
    super();
    this._onChipChange = this._onChipChange.bind(this);
    this._onFreeInput = this._onFreeInput.bind(this);
    this._onSubmitCapture = this._onSubmitCapture.bind(this);
  }

  connectedCallback() {
    this._formCache = null;
    this._chips = Array.from(this.querySelectorAll('[data-gc-chip]'));
    this._freeInput = this.querySelector('[data-gc-free]');
    this._errorEl = this.querySelector('[data-gc-error]');
    this._valueLabel = this.querySelector('[data-gc-selected-value]');
    this._minAmount = parseInt(this.getAttribute('min-amount'), 10) || 1;
    this._maxAmount = parseInt(this.getAttribute('max-amount'), 10) || 2000;
    this._seq = 0;
    this._debounceTimer = null;
    this._lookupController = null;

    try {
      this._selectedVariant = JSON.parse(this.querySelector('script[data-variant]')?.textContent || 'null');
    } catch (e) {
      this._selectedVariant = null;
    }

    // Valid initial state: an active chip or a prefilled free amount (?variant=).
    this._state = 'valid';

    this._chips.forEach((chip) => chip.addEventListener('change', this._onChipChange));
    if (this._freeInput) this._freeInput.addEventListener('input', this._onFreeInput);
    // Capture on document so this runs before the theme's ProductForm submit handler.
    document.addEventListener('submit', this._onSubmitCapture, true);

    this._initialSync();
    this._preloadChipRenders();
  }

  disconnectedCallback() {
    document.removeEventListener('submit', this._onSubmitCapture, true);
    clearTimeout(this._debounceTimer);
    if (this._lookupController) this._lookupController.abort();
    this._seq += 1; // invalidate in-flight renders
  }

  /* -------------------- initial state -------------------- */

  // The server renders prices for product.selected_or_first_available_variant
  // (the 1 € variant unless ?variant= is present). If the intended initial
  // selection (default 25 € chip) differs, sync the form + displayed price once.
  _initialSync() {
    const activeId = this.getAttribute('active-variant-id');
    if (!activeId) return;
    const idInput = this._idInput();
    if (idInput && idInput.value !== activeId) {
      this._setFormVariant(activeId);
    }
    if (activeId !== this.getAttribute('rendered-variant-id')) {
      this._seq += 1;
      this._renderVariant(activeId, this._seq);
    }
  }

  // Warm the section-render cache for the preset chips so chip clicks update
  // the price instantly (mirrors the theme's own variant preloading).
  _preloadChipRenders() {
    const preload = () => {
      this._chips.forEach((chip) => {
        this._fetchRender(chip.getAttribute('data-variant-id')).catch(() => {});
      });
    };
    if (window.requestIdleCallback) {
      window.requestIdleCallback(preload, { timeout: 2000 });
    } else {
      setTimeout(preload, 500);
    }
  }

  /* -------------------- event handlers -------------------- */

  _onChipChange(event) {
    const chip = event.target;
    if (!chip.checked) return;
    // Mutual exclusivity: a chip click clears the free field.
    clearTimeout(this._debounceTimer);
    if (this._lookupController) this._lookupController.abort();
    if (this._freeInput) this._freeInput.value = '';
    this._hideError();
    this._setState('valid');
    this._updateValueLabel(chip.value);

    // The chip's variant ID is known from Liquid: the cart form is correct
    // immediately, the price display catches up via section re-render.
    const variantId = chip.getAttribute('data-variant-id');
    this._setFormVariant(variantId);
    this._updateUrl(variantId);
    this._seq += 1;
    this._renderVariant(variantId, this._seq);
  }

  _onFreeInput() {
    // Mutual exclusivity: typing deselects any chip.
    this._chips.forEach((chip) => { chip.checked = false; });
    clearTimeout(this._debounceTimer);
    if (this._lookupController) this._lookupController.abort();
    this._seq += 1;
    const seq = this._seq;

    const raw = this._freeInput.value.trim();
    if (raw === '') {
      // No selection at all: disable add-to-cart without shouting an error.
      this._hideError();
      this._setState('invalid');
      return;
    }

    // Whole euros only (variants exist in 1 € steps), within [min, max].
    const amount = /^\d+$/.test(raw) ? parseInt(raw, 10) : NaN;
    if (!Number.isInteger(amount) || amount < this._minAmount || amount > this._maxAmount) {
      this._showError(this.getAttribute('error-range'));
      this._setState('invalid');
      return;
    }

    this._hideError();
    this._setState('pending');
    this._updateValueLabel(String(amount));
    this._debounceTimer = setTimeout(() => {
      this._resolveFreeAmount(amount, seq);
    }, 300);
  }

  // Blocks submission (incl. Enter/programmatic submit) while the selection is
  // invalid or a lookup is in flight. Runs in the capture phase before the
  // theme's ProductForm ajax handler.
  _onSubmitCapture(event) {
    if (event.target !== this._form || this._state === 'valid') return;
    event.preventDefault();
    event.stopPropagation();
    if (this._state === 'invalid' && this._freeInput && !this._errorEl?.textContent) {
      this._showError(this.getAttribute('error-range'));
    }
    if (this._freeInput) this._freeInput.focus();
  }

  /* -------------------- variant resolution -------------------- */

  // Resolves "{amount} €" to a variant via the tokenless Storefront API.
  // Liquid cannot see variants beyond the first 250, so this is the only
  // reliable path for free amounts (per Shopify's >250-variants guidance).
  async _resolveFreeAmount(amount, seq) {
    this._lookupController = new AbortController();
    let variant = null;
    try {
      const response = await fetch(`/api/${BBGiftcardAmountSelector.STOREFRONT_API_VERSION}/graphql.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: this._lookupController.signal,
        body: JSON.stringify({
          query: `query GcVariant($handle: String!, $name: String!, $value: String!) {
            product(handle: $handle) {
              variantBySelectedOptions(selectedOptions: [{ name: $name, value: $value }]) {
                id
                availableForSale
              }
            }
          }`,
          variables: {
            handle: this.getAttribute('product-handle'),
            name: this.getAttribute('option-name'),
            value: `${amount} €`
          }
        })
      });
      if (seq !== this._seq) return;
      if (!response.ok) throw new Error(`Storefront API responded with ${response.status}`);
      const json = await response.json();
      if (json.errors?.length) throw new Error('Storefront API returned errors');
      variant = json.data?.product?.variantBySelectedOptions ?? null;
    } catch (error) {
      if (error.name === 'AbortError' || seq !== this._seq) return;
      this._showError(this.getAttribute('error-generic'));
      this._setState('invalid');
      return;
    }

    if (seq !== this._seq) return;

    // Defensive: shouldn't happen within 1–2000, but the variant may be
    // missing or not available for sale.
    if (!variant || !variant.availableForSale) {
      this._showError(this.getAttribute('error-unavailable'));
      this._setState('invalid');
      return;
    }

    // The Storefront API returns a GID (gid://shopify/ProductVariant/123…):
    // strip it to the numeric ID for the cart form.
    const variantId = variant.id.split('/').pop();
    this._setFormVariant(variantId);
    this._updateUrl(variantId);
    this._setState('valid');
    this._renderVariant(variantId, seq);
  }

  /* -------------------- theme integration -------------------- */

  // Lazy + self-healing: the form sits after this element in the DOM and can
  // be re-created by full product re-renders.
  get _form() {
    if (!this._formCache || !this._formCache.isConnected) {
      this._formCache = document.forms[this.getAttribute('form-id')] || null;
    }
    return this._formCache;
  }

  _idInput() {
    return this._form ? this._form.elements.namedItem('id') : null;
  }

  _setFormVariant(variantId) {
    const idInput = this._idInput();
    if (!idInput) return;
    idInput.value = variantId;
    idInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  _updateUrl(variantId) {
    if (!this.hasAttribute('update-url') || !variantId) return;
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('variant', variantId);
    window.history.replaceState({ path: newUrl.toString() }, '', newUrl.toString());
  }

  _updateValueLabel(amount) {
    if (this._valueLabel) this._valueLabel.textContent = `${amount} €`;
  }

  // Fetches the section rendered for the given variant and dispatches the
  // theme-native events. ProductRerender then swaps price/meta, payment
  // container, sticky form etc. — identical to the standard variant picker.
  async _renderVariant(variantId, seq) {
    let html;
    try {
      html = await this._fetchRender(variantId);
    } catch (error) {
      // Price display stays as-is; the cart form already has the right ID.
      return;
    }
    if (seq !== this._seq || !this._form) return;

    const fragment = document.createRange().createContextualFragment(html);
    const newSelf = this._deepQuery(fragment, `bb-giftcard-amount-selector[form-id="${this.getAttribute('form-id')}"]`);
    let variant = null;
    try {
      variant = JSON.parse(newSelf?.querySelector('script[data-variant]')?.textContent || 'null');
    } catch (e) {
      variant = null;
    }
    const previousVariant = this._selectedVariant;
    if (variant) this._selectedVariant = variant;

    this._form.dispatchEvent(new CustomEvent('variant:change', {
      bubbles: true,
      detail: { formId: this.getAttribute('form-id'), variant: this._selectedVariant, previousVariant }
    }));
    this._form.dispatchEvent(new CustomEvent('variant:changed', {
      bubbles: true,
      detail: { formId: this.getAttribute('form-id'), variant: this._selectedVariant, previousVariant }
    }));
    this._form.dispatchEvent(new CustomEvent('product:rerender', {
      detail: { htmlFragment: fragment, productChange: false }
    }));

    // Re-apply gating: product:rerender may have swapped the payment container.
    this._applyGating();
    if (window.Shopify && window.Shopify.PaymentButton) window.Shopify.PaymentButton.init();
  }

  _fetchRender(variantId) {
    const sectionId = this.getAttribute('section-id');
    const isQuickBuy = this.getAttribute('context') === 'quick-buy';
    const cacheKey = `${isQuickBuy ? 'page' : sectionId}:${variantId}`;
    const cache = BBGiftcardAmountSelector._renderCache;

    if (!cache.has(cacheKey)) {
      const url = new URL(this.getAttribute('product-url'), window.location.origin);
      url.searchParams.set('variant', variantId);
      if (!isQuickBuy) url.searchParams.set('section_id', sectionId);
      const promise = fetch(url.toString()).then((response) => {
        if (!response.ok) throw new Error(`Section render failed with ${response.status}`);
        return response.text();
      });
      // Evict on failure so a transient error is not cached forever.
      promise.catch(() => cache.delete(cacheKey));
      cache.set(cacheKey, promise);
      if (cache.size > 30) cache.delete(cache.keys().next().value);
    }
    return cache.get(cacheKey);
  }

  // querySelector that also pierces <template> contents (quick-buy markup
  // lives inside a template in full-page renders).
  _deepQuery(root, selector) {
    const direct = root.querySelector(selector);
    if (direct) return direct;
    for (const template of root.querySelectorAll('template')) {
      const found = this._deepQuery(template.content, selector);
      if (found) return found;
    }
    return null;
  }

  /* -------------------- state / UI -------------------- */

  _setState(state) {
    this._state = state;
    this._applyGating();
  }

  _applyGating() {
    if (!this._form) return;
    const disable = this._state !== 'valid';
    Array.from(this._form.elements)
      .filter((element) => element.type === 'submit')
      .forEach((button) => button.toggleAttribute('disabled', disable));
    this._form.querySelectorAll('.shopify-payment-button').forEach((wrapper) => {
      wrapper.classList.toggle('gc-payment-disabled', disable);
    });
    this.classList.toggle('gc-amount-selector--pending', this._state === 'pending');
  }

  _showError(message) {
    if (!this._errorEl) return;
    this._errorEl.textContent = message || '';
    this._errorEl.hidden = false;
    if (this._freeInput) this._freeInput.setAttribute('aria-invalid', 'true');
  }

  _hideError() {
    if (!this._errorEl) return;
    this._errorEl.textContent = '';
    this._errorEl.hidden = true;
    if (this._freeInput) this._freeInput.removeAttribute('aria-invalid');
  }
}

if (!customElements.get('bb-giftcard-amount-selector')) {
  customElements.define('bb-giftcard-amount-selector', BBGiftcardAmountSelector);
}

/* ------------------------------------------------------------------ */
/* BB back in stock — subscription form in the drawer                  */
/* (snippets/bb-back-in-stock-drawer.liquid). The trigger              */
/* (bb_back_in_stock block) opens the drawer natively via              */
/* toggle-button/aria-controls. The form follows the selected variant  */
/* of the main product: "variant:change" events are scoped to the      */
/* section of the trigger (like the availability toggle below), so     */
/* quick buy forms on the same page are ignored. Submitting posts JSON */
/* to the app proxy (app "kitzheimat-migration"), which answers        */
/* { ok, status: 'pending' (confirmation email sent) | 'active' }.     */
/* ------------------------------------------------------------------ */
class BBBackInStockForm extends HTMLElement {
  constructor() {
    super();
    this._onSubmit = this._onSubmit.bind(this);
    this._onVariantChange = this._onVariantChange.bind(this);
  }

  connectedCallback() {
    this._form = this.querySelector('[data-bis-form]');
    this._busy = false;
    if (this._form) this._form.addEventListener('submit', this._onSubmit);
    document.addEventListener('variant:change', this._onVariantChange);
  }

  disconnectedCallback() {
    if (this._form) this._form.removeEventListener('submit', this._onSubmit);
    document.removeEventListener('variant:change', this._onVariantChange);
  }

  get _submitButton() {
    return this._form ? this._form.querySelector('[type="submit"]') : null;
  }

  _onVariantChange(event) {
    const trigger = document.querySelector('.bb-back-in-stock');
    const section = trigger && trigger.closest('.shopify-section');
    if (!section || !section.contains(event.target)) return;

    // A combination that doesn't exist comes as null or {}: nothing to subscribe to.
    const variant = event.detail && event.detail.variant;
    const variantId = variant && variant.id ? String(variant.id) : '';
    this.setAttribute('variant-id', variantId);

    const variantLine = this.querySelector('[data-bis-variant]');
    const variantTitle = this.querySelector('[data-bis-variant-title]');
    if (variantLine) variantLine.hidden = !variantId;
    if (variantTitle && variantId) variantTitle.textContent = variant.title || '';

    const button = this._submitButton;
    if (button && !this._busy) button.disabled = !variantId;

    // Another variant is another sign-up: show the form again.
    this._showState(null);
  }

  /** null = form, 'pending' / 'active' = matching success message. */
  _showState(state) {
    if (this._form) this._form.hidden = state !== null;
    this.querySelectorAll('[data-bis-success]').forEach((element) => {
      element.hidden = element.getAttribute('data-bis-success') !== state;
    });
  }

  /** Loading state like the theme's loader-button: spinner instead of the label (see custom.css). */
  _setBusy(busy) {
    this._busy = busy;
    const button = this._submitButton;
    if (!button) return;
    const loader = button.querySelector('.loader-button__loader');
    if (loader) loader.hidden = !busy;
    if (busy) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
    // _busy already blocks a second submit; disabled is only for "no variant selected".
    button.disabled = !this.getAttribute('variant-id');
  }

  async _onSubmit(event) {
    event.preventDefault();
    const variantId = this.getAttribute('variant-id');
    if (this._busy || !variantId) return;

    const error = this.querySelector('[data-bis-error]');
    if (error) error.hidden = true;
    this._setBusy(true);

    try {
      const response = await fetch(this.getAttribute('action'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          email: new FormData(this._form).get('email'),
          variantId,
          locale: this.getAttribute('locale'),
          rootUrl: this.getAttribute('root-url')
        })
      });
      const result = await response.json().catch(() => null);
      if (!response.ok || !result || !result.ok) throw new Error(`Back in stock sign-up failed (${response.status})`);

      this._showState(result.status === 'active' ? 'active' : 'pending');
    } catch (e) {
      console.error(e);
      if (error) error.hidden = false;
    } finally {
      this._setBusy(false);
    }
  }
}

if (!customElements.get('bb-back-in-stock-form')) {
  customElements.define('bb-back-in-stock-form', BBBackInStockForm);
}

/* ------------------------------------------------------------------ */
/* BB back in stock — variant availability toggle. When the block is   */
/* set to "only when unavailable", the trigger wrapper is always       */
/* rendered (with [data-only-when-unavailable], hidden while the       */
/* selected variant is in stock — see snippets/product-form.liquid).   */
/* The theme's variant picker dispatches a bubbling "variant:change"   */
/* event on the product form on every variant switch; we listen for it */
/* here and show the trigger whenever the newly selected variant is    */
/* sold out (or the combination doesn't exist), and hide it again when */
/* an available variant is picked. Scoped to the form's section so     */
/* other product forms on the page don't affect the trigger.           */
/* ------------------------------------------------------------------ */
document.addEventListener('variant:change', (event) => {
  const form = event.target;
  const variant = event.detail && event.detail.variant;

  document.querySelectorAll('.bb-back-in-stock[data-only-when-unavailable]').forEach((trigger) => {
    const section = trigger.closest('.shopify-section');
    if (section && !section.contains(form)) return;
    trigger.hidden = !!(variant && variant.available);
  });
});

/* ------------------------------------------------------------------ */
/* BB gift card PDF — on the gift card page, the print button          */
/* downloads the PDF template from the shop metafield                  */
/* custom.giftcard_pdf with the placeholder words "Code", "Wert" and   */
/* "Datum" replaced by the gift card code, its initial value and the   */
/* "Einlösbar bis" date. Everything runs client-side:                  */
/*   - pdf.js locates the placeholder words (text + coordinates)      */
/*   - pdf-lib covers them with white and draws the replacement text  */
/* Both libraries are theme assets (Shopify CDN) and are only loaded  */
/* when the button is clicked. While working, the loader-button       */
/* spinner is shown. On any failure we fall back to window.print().   */
/* ------------------------------------------------------------------ */
class BBGiftcardPdf extends HTMLElement {
  static _scripts = new Map();

  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    this._button = this.querySelector('button');
    this._busy = false;
    if (this._button) this._button.addEventListener('click', this._onClick);

    // ?download=1 forwards to the generated PDF directly on page load (shown
    // in the browser's PDF viewer, e.g. for links in gift card emails). The
    // param is stripped so navigating back doesn't re-trigger it. Deferred by
    // a tick so a <bb-giftcard-motiv-chooser> on the page has applied its
    // ?motiv= / ?lang= preselection (pdf-url, filename, expires) first.
    const params = new URLSearchParams(window.location.search);
    if (params.get('download') === '1') {
      const url = new URL(window.location.href);
      url.searchParams.delete('download');
      window.history.replaceState({}, '', url.toString());
      setTimeout(() => this._generate('view'), 0);
    }
  }

  disconnectedCallback() {
    if (this._button) this._button.removeEventListener('click', this._onClick);
  }

  // Placeholder → replacement map (word-boundary, case-sensitive).
  get _replacements() {
    return {
      Code: this.getAttribute('code') || '',
      Wert: this.getAttribute('value') || '',
      Datum: this.getAttribute('expires') || ''
    };
  }

  _onClick() {
    this._generate('download');
  }

  // mode: 'download' saves the file, 'view' navigates to the generated PDF
  // so the browser shows it inline.
  async _generate(mode) {
    if (this._busy) return;
    this._busy = true;
    this._setLoading(true);
    let navigating = false;

    try {
      const output = await this._buildFilled(this.getAttribute('pdf-url'));

      const blob = new Blob([output], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      if (mode === 'view') {
        // Keep the spinner running — we're leaving the page.
        navigating = true;
        window.location.assign(url);
        return;
      }

      const link = document.createElement('a');
      link.href = url;
      link.download = this.getAttribute('filename') || 'Geschenkgutschein.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      console.warn('[bb-giftcard-pdf] Falling back to window.print():', error);
      window.print();
    } finally {
      if (!navigating) {
        this._setLoading(false);
        this._busy = false;
      }
    }
  }

  /* Locate the placeholder words with pdf.js (PDF user space, y-up —
     the same coordinate system pdf-lib draws in). */
  async _locatePlaceholders(bytes) {
    const pdfjs = window.pdfjsLib;
    pdfjs.GlobalWorkerOptions.workerSrc = this.getAttribute('pdfjs-worker-src');

    // pdf.js transfers the buffer to its worker, so hand over a copy.
    const doc = await pdfjs.getDocument({ data: bytes.slice() }).promise;
    const placements = [];
    const keys = Object.keys(this._replacements);
    const isWordChar = (char) => char !== undefined && /[A-Za-z0-9ÄÖÜäöüß]/.test(char);

    try {
      for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber += 1) {
        const page = await doc.getPage(pageNumber);
        const { width: pageWidth } = page.getViewport({ scale: 1 });
        const textContent = await page.getTextContent();

        for (const item of textContent.items) {
          const str = item.str;
          if (!str) continue;
          const fontSize = Math.hypot(item.transform[2], item.transform[3]) || item.height;
          if (!fontSize || !item.width) continue;

          for (const key of keys) {
            let index = str.indexOf(key);
            while (index !== -1) {
              // Whole-word, case-sensitive match ("Code" must not hit "Gutscheincode").
              if (!isWordChar(str[index - 1]) && !isWordChar(str[index + key.length])) {
                // Approximate the segment position proportionally within the run.
                const x = item.transform[4] + (item.width * index) / str.length;
                const width = (item.width * key.length) / str.length;
                placements.push({
                  pageIndex: pageNumber - 1,
                  key,
                  x,
                  width,
                  baseline: item.transform[5],
                  fontSize,
                  pageWidth
                });
              }
              index = str.indexOf(key, index + key.length);
            }
          }
        }
      }
    } finally {
      doc.destroy();
    }
    return placements;
  }

  /* Cover each placeholder with white and draw the replacement with pdf-lib. */
  async _writeReplacements(bytes, placements) {
    const { PDFDocument, StandardFonts, rgb } = window.PDFLib;
    const doc = await PDFDocument.load(bytes);
    const font = await doc.embedFont(StandardFonts.Helvetica);
    const pages = doc.getPages();
    const replacements = this._replacements;

    placements.forEach((placement) => {
      const page = pages[placement.pageIndex];
      if (!page) return;
      const text = replacements[placement.key];
      const size = placement.fontSize;

      // White-out the placeholder (cover ascenders and descenders).
      page.drawRectangle({
        x: placement.x - 1,
        y: placement.baseline - 0.28 * size,
        width: placement.width + 2,
        height: 1.32 * size,
        color: rgb(1, 1, 1)
      });

      if (!text) return;
      const textWidth = font.widthOfTextAtSize(text, size);

      // Placeholders that sit around the horizontal page center (like the big
      // "Wert"/"Code" headings) stay centered; others keep their left edge
      // (e.g. "Datum" after "Einlösbar bis:").
      const segmentCenter = placement.x + placement.width / 2;
      const isCentered = Math.abs(segmentCenter - placement.pageWidth / 2) < placement.pageWidth * 0.12;
      const x = isCentered ? segmentCenter - textWidth / 2 : placement.x;

      page.drawText(text, {
        x,
        y: placement.baseline,
        size,
        font,
        color: rgb(0, 0, 0)
      });
    });

    return doc.save();
  }

  /* Fetches a template and resolves with the filled PDF bytes (placeholders
     replaced). Libraries and template are loaded on demand and in parallel.
     Cached per template URL + replacement values, so repeated downloads of
     the same motif/language don't refetch or refill. */
  _buildFilled(pdfUrl) {
    if (!pdfUrl) return Promise.reject(new Error('Missing pdf-url'));
    const key = `${pdfUrl}|${JSON.stringify(this._replacements)}`;
    if (!this._filled) this._filled = new Map();
    if (!this._filled.has(key)) {
      const job = Promise.all([
        fetch(pdfUrl).then((response) => {
          if (!response.ok) throw new Error(`PDF template fetch failed with ${response.status}`);
          return response.arrayBuffer();
        }),
        this._loadScript(this.getAttribute('pdfjs-src')),
        this._loadScript(this.getAttribute('pdflib-src'))
      ]).then(async ([pdfBytes]) => {
        const bytes = new Uint8Array(pdfBytes);
        const placements = await this._locatePlaceholders(bytes);
        return this._writeReplacements(bytes, placements);
      });
      job.catch(() => this._filled.delete(key));
      this._filled.set(key, job);
    }
    return this._filled.get(key);
  }

  _setLoading(loading) {
    if (!this._button) return;
    if (loading) {
      this._button.setAttribute('aria-busy', 'true');
      this._button.setAttribute('disabled', 'disabled');
    } else {
      this._button.removeAttribute('aria-busy');
      this._button.removeAttribute('disabled');
    }
  }

  _loadScript(src) {
    if (!src) return Promise.reject(new Error('Missing script src'));
    if (!BBGiftcardPdf._scripts.has(src)) {
      BBGiftcardPdf._scripts.set(src, new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => {
          BBGiftcardPdf._scripts.delete(src);
          reject(new Error(`Failed to load ${src}`));
        };
        document.head.appendChild(script);
      }));
    }
    return BBGiftcardPdf._scripts.get(src);
  }
}

if (!customElements.get('bb-giftcard-pdf')) {
  customElements.define('bb-giftcard-pdf', BBGiftcardPdf);
}

/* ------------------------------------------------------------------ */
/* BB gift card motif chooser — on the gift card page, lets the        */
/* customer pick the print motif (shop metaobjects "giftcard_motiv",   */
/* rendered by sections/main-gift-card.liquid) and the language of the */
/* PDF (de/en). Each radio carries the per-language template URL and  */
/* motif images in data attributes. On change the element swaps the   */
/* big preview image (attribute "preview") + thumbnails and writes    */
/* pdf-url, filename and expires onto the <bb-giftcard-pdf> element   */
/* (attribute "target"), which fills that template when the print     */
/* button is clicked.                                                 */
/* ?motiv=<handle>&lang=de|en preselect a motif/language and are kept */
/* in sync in the URL, so the link can be shared and the ?download=1  */
/* email link opens the right template. Motifs without a template in  */
/* the chosen language are hidden.                                    */
/* ------------------------------------------------------------------ */
class BBGiftcardMotivChooser extends HTMLElement {
  constructor() {
    super();
    this._onChange = this._onChange.bind(this);
  }

  connectedCallback() {
    this._target = document.getElementById(this.getAttribute('target'));
    this._preview = document.getElementById(this.getAttribute('preview'));
    this._langSelect = this.querySelector('[data-motiv-lang]');
    this._radios = Array.from(this.querySelectorAll('input[type="radio"]'));

    const params = new URLSearchParams(window.location.search);
    const lang = params.get('lang');
    if (lang && this._langSelect && this._langSelect.querySelector(`option[value="${lang}"]`)) {
      this._langSelect.value = lang;
    }
    const motiv = params.get('motiv');
    const preselected = motiv ? this._radios.find((radio) => radio.value === motiv) : null;
    if (preselected) preselected.checked = true;

    this.addEventListener('change', this._onChange);
    this._apply(false);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this._onChange);
  }

  get lang() {
    const value = this._langSelect ? this._langSelect.value : this.getAttribute('default-lang');
    return value === 'en' ? 'en' : 'de';
  }

  _onChange() {
    this._apply(true);
  }

  _option(radio) {
    return radio.closest('.gift-card__motiv-option') || radio;
  }

  _apply(updateUrl) {
    const lang = this.lang;
    const suffix = lang === 'en' ? 'En' : 'De';

    this._radios.forEach((radio) => {
      const option = this._option(radio);
      const available = Boolean(radio.dataset[`pdf${suffix}`]);
      option.hidden = !available;
      if (!available && radio.checked) radio.checked = false;

      const thumb = option.querySelector('img');
      const thumbSrc = radio.dataset[`thumb${suffix}`] || radio.dataset.thumbDe;
      if (thumb && thumbSrc && thumb.getAttribute('src') !== thumbSrc) thumb.src = thumbSrc;
    });

    let selected = this._radios.find((radio) => radio.checked);
    if (!selected) {
      selected = this._radios.find((radio) => !this._option(radio).hidden);
      if (selected) selected.checked = true;
    }
    if (!selected) return;

    if (this._target) {
      this._target.setAttribute('pdf-url', selected.dataset[`pdf${suffix}`]);
      const filename = this.getAttribute(`filename-${lang}`);
      if (filename) this._target.setAttribute('filename', filename);
      const expires = this.getAttribute(`expires-${lang}`);
      if (expires !== null) this._target.setAttribute('expires', expires);
    }

    if (this._preview) {
      const src = selected.dataset[`preview${suffix}`] || selected.dataset.previewDe;
      if (src && this._preview.getAttribute('src') !== src) {
        this._preview.removeAttribute('srcset');
        this._preview.removeAttribute('sizes');
        this._preview.src = src;
      }
      const name = this._option(selected).querySelector('.gift-card__motiv-name');
      if (name) this._preview.alt = name.textContent.trim();
    }

    if (updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('motiv', selected.value);
      url.searchParams.set('lang', lang);
      window.history.replaceState({}, '', url.toString());
    }
  }
}

if (!customElements.get('bb-giftcard-motiv-chooser')) {
  customElements.define('bb-giftcard-motiv-chooser', BBGiftcardMotivChooser);
}

/* ------------------------------------------------------------------ */
/* Pandectes CMP (cookie banner) — style override. The banner renders  */
/* inside the closed-over shadow root of <pandectes-cmp>, so regular   */
/* theme CSS can't reach it. We inject a <style> node into the shadow  */
/* root to render the "deny" button in the notice as a secondary       */
/* (inverted) button. A MutationObserver waits for the element (and    */
/* its shadow root) since the CMP script loads asynchronously; it      */
/* disconnects once the styles are in.                                 */
/* ------------------------------------------------------------------ */
(() => {
  const css = `
    [data-role="notice"] .deny.primary,
    dialog.notice .deny.primary {
      --color: var(--color-secondary);
      --color-contrast: var(--color-secondary-content);
      background-color: var(--color-contrast);
      color: var(--color);
    }
  `;

  let observer = null;

  const tryInject = () => {
    const cmp = document.querySelector('pandectes-cmp');
    if (!cmp || !cmp.shadowRoot) return false;

    if (!cmp.shadowRoot.querySelector('#injected-pandectes-styles')) {
      const styleNode = document.createElement('style');
      styleNode.id = 'injected-pandectes-styles';
      styleNode.textContent = css;
      cmp.shadowRoot.appendChild(styleNode);
    }

    if (observer) observer.disconnect();
    return true;
  };

  if (tryInject()) return;

  observer = new MutationObserver(() => tryInject());
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
